import { Injectable, inject } from '@angular/core';
import { EMPTY, map, Observable, of, startWith, switchMap } from 'rxjs';
import { UserContext } from '../models/user-context';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { UserContextStorage } from '../services/user-context.storage';
import { Permission } from '../enums/permission';
import { Role } from '../enums/role';

@Injectable({
  providedIn: 'root',
})
export class UserInfoProviderService {
  private readonly userContextStorage = inject(UserContextStorage);

  public context(): Observable<UserContext> {
    return this.userContextStorage.fetch().pipe(
      switchMap((context) => {
        if (isNil(context)) {
          return EMPTY;
        }

        return of(context);
      }),
    );
  }

  public isLoggedIn(): Observable<boolean> {
    return this.userContextStorage
      .fetch()
      .pipe(map((context) => !isNil(context)));
  }

  public id(): Observable<Guid> {
    return this.userContextStorage.fetch().pipe(
      switchMap((context) => {
        if (isNil(context)) {
          return EMPTY;
        }

        const { id } = context;

        return of(id);
      }),
    );
  }

  public email(): Observable<string> {
    return this.userContextStorage.fetch().pipe(
      switchMap((context) => {
        if (isNil(context)) {
          return EMPTY;
        }

        const { email } = context;

        return of(email);
      }),
    );
  }

  public displayName(): Observable<string> {
    return this.userContextStorage.fetch().pipe(
      switchMap((context) => {
        if (isNil(context)) {
          return EMPTY;
        }

        const { displayName } = context;

        return of(displayName);
      }),
      startWith('Nieznany'),
    );
  }

  public isSubscriber(): Observable<boolean> {
    return this.userContextStorage.fetch().pipe(
      map((context) => {
        return valueOrDefault(context?.roles.includes(Role.Subscriber), false);
      }),
    );
  }

  public avatar(): Observable<Nillable<string>> {
    return this.userContextStorage.fetch().pipe(
      switchMap((context) => {
        if (isNil(context)) {
          return EMPTY;
        }

        const { avatar } = context;

        return of(avatar);
      }),
    );
  }

  public permissions(): Permission[] {
    const context = this.userContextStorage.fetchSync();

    if (isNil(context)) {
      return [];
    }

    return context.permissions;
  }
}
